/* eslint-disable jsx-a11y/media-has-caption */
import {
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Box, Grid } from '@mui/material';
import BlockProps from '../../../models/BlockProps';
import BlockVideoData from './BlockVideoData';
import VideoSettingsModal from '../../Modals/VideoSettingsModal/VideoSettingsModal';
import EditableText from '../../Form/EditableText';

function BlockVideo({
  data,
  isEditing,
  isTemplate,
  editHandler,
}: BlockProps) {
  const blockId = data.id;
  const props = (data.data as BlockVideoData);

  const videoRef = createRef<HTMLVideoElement>();
  const [showModal, setShowModal] = useState(false);
  const [videoSrc, setVideoSrc] = useState(props.video.src);

  const [minVideoHeight, setMinVideoHeight] = useState(250);
  const textRef = useRef(null);

  useEffect(() => {
    const updateVideoHeight = () => {
      if (textRef.current) {
        const textHeight = textRef.current.offsetHeight + 24;
        setMinVideoHeight(Math.max(250, textHeight));
      }
    };

    const observer = new ResizeObserver(updateVideoHeight);
    if (textRef.current) observer.observe(textRef.current);

    window.addEventListener('resize', updateVideoHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateVideoHeight);
    };
  }, []);

  const reloadVideo = useCallback(() => {
    if (videoRef.current) videoRef.current.load();
  }, [videoRef]);

  useEffect(() => {
    if (props.video.settings?.muted) {
      videoRef.current.defaultMuted = true;
    }
  }, [videoRef, props.video.settings?.muted]);

  return (
    <>
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          minHeight: '250px',
          overflow: 'hidden',
          backgroundPosition: 'center 0px',
          position: 'relative',
        }}
      >
        <Grid
          container
          spacing={0}
          direction="row"
          alignItems="stretch"
          justifyContent="space-between"
          sx={{
            minHeight: '250px !important',
            width: '100%',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '250px',
            }}
          >
            {isEditing && !isTemplate && (
              <VideoSettingsModal
                title={props.title}
                video={props.video}
                showModal={showModal}
                setShowModal={setShowModal}
                onConfirm={(
                  title,
                  videoSrc,
                  backgroundColor,
                  loop,
                  autoPlay,
                  muted,
                  staticBackground,
                ) => {
                  props.title.value = title;
                  props.video.src = videoSrc;
                  props.title.settings.backgroundColor = backgroundColor;
                  props.video.settings.loop = loop;
                  props.video.settings.autoplay = autoPlay;
                  props.video.settings.muted = muted;
                  props.video.settings.staticBackground = staticBackground;

                  setVideoSrc(videoSrc);
                  editHandler(blockId, props);
                  reloadVideo();
                }}
              />
            )}

            <Box
              sx={{
                position: 'relative',
                width: '100%',
                minHeight: '250px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {props.title && (
                <Box
                  ref={textRef}
                  sx={{
                    position: 'absolute',
                    textAlign: 'center',
                    background: props.title.settings?.backgroundColor,
                    padding: '12px 16px',
                    zIndex: 2,
                    pointerEvents: 'none',
                    maxWidth: '90%',
                  }}
                >
                  <EditableText
                    text={props.title.value}
                    textColor={props.title.settings?.textColor}
                    isEditable={false}
                    updateHandler={() => { }}
                  />
                </Box>
              )}

              <Box
                sx={{
                  width: '100%',
                  minHeight: `${minVideoHeight}px`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <video
                  ref={videoRef}
                  loop={!!props.video.settings?.loop}
                  playsInline
                  autoPlay={!!props.video.settings?.autoplay}
                  style={{
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                    minHeight: `${minVideoHeight}px`,
                  }}
                  onClick={() => { if (isEditing) setShowModal(true); }}
                >
                  <source src={videoSrc} type="video/mp4" />
                </video>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default BlockVideo;
